import React, { useMemo, useContext } from 'react';
import { calculateTournamentRankings } from '../utils/tournamentScoring';
import { useTournamentAppConfiguration } from '../utils/tournamentconfig/TournamentConfiguration';
import { FriendsContext } from '../contexts/FriendsContext';
import { ResultsContext } from '../contexts/ResultsContext';
import './FriendCard.css';

/**
 * FriendCard component displays a summary of a friend's performance in the tournament
 * 
 * @param {Object} props - Component props
 * @param {string} props.username - Username of the friend
 * @param {Array} props.games - Array of games in the tournament
 * @param {string} props.theme - Current theme (dark/light)
 * @param {boolean} props.isCurrentUser - Whether this card represents the current user
 * @param {boolean} props.isSelected - Whether this player is selected for comparison
 * @param {Function} props.onCompare - Function to trigger comparison mode
 */
const FriendCard = ({ username, games, theme, isCurrentUser, isSelected, onCompare, sortOrder = 'default' }) => {
  const tournamentAppConfiguration = useTournamentAppConfiguration();
  const { removeFriend } = useContext(FriendsContext);
  const { results } = useContext(ResultsContext);

  // Calculate summary stats
  const summary = useMemo(() => {
    return calculateTournamentRankings(games, tournamentAppConfiguration, username, '-');
  }, [games, tournamentAppConfiguration, username]);

  // Extract game-specific data for this friend
  const gamePerformance = useMemo(() => {
    if (!games || !username) return [];

    const performance = games.map(game => {
      const userEntry = game.leaderboard?.find(entry => 
        entry.userName.toLowerCase() === username.toLowerCase()
      );

      return {
        id: game.id || game.title,
        title: game.title,
        rank: userEntry ? userEntry.rank : null,
        score: userEntry ? userEntry.score : null,
        points: userEntry ? userEntry.points : 0
      };
    });

    // Sort based on sortOrder
    if (sortOrder === 'alpha') {
      return performance.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortOrder === 'rank') {
      return performance.sort((a, b) => {
        const rankA = a.rank === null ? Infinity : a.rank;
        const rankB = b.rank === null ? Infinity : b.rank;
        
        if (rankA !== rankB) {
          return rankA - rankB;
        }
        // If ranks are equal, fall back to alphabetical
        return (a.title || '').localeCompare(b.title || '');
      });
    }

    return performance;
  }, [games, username, sortOrder]);

  const handleRemove = (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to remove ${username} from your friends?`)) {
      removeFriend(username);
    }
  };

  if (!username) return null;

  return (
    <div className={`friend-card ${theme} ${isSelected ? 'selected' : ''}`}>
      <div className="friend-card-actions">
        <button 
          className={`compare-friend-btn ${isSelected ? 'active' : ''}`} 
          onClick={(e) => { e.stopPropagation(); onCompare(); }}
          title={isSelected ? 'Deselect' : `Compare ${username}`}
        >
          {isSelected ? 'Selected' : 'Compare'}
        </button>
        {!isCurrentUser && (
          <button 
            className="remove-friend-btn" 
            onClick={handleRemove}
            title={`Remove ${username} from friends`}
          >
            &times;
          </button>
        )}
      </div>
      <div className="friend-card-header">
        <div className="friend-info">
          <div className="friend-name-container">
            {results?.users?.[username]?.avatar_path && (
              <div 
                className="friend-avatar-circle" 
                style={{ backgroundColor: results.users[username].background_color_hex }}
              >
                <img 
                  src={results.users[username].avatar_path} 
                  alt={username} 
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <h3 
              className="friend-name"
              style={{ color: results?.users?.[username]?.background_color_hex }}
            >
              {username}
            </h3>
          </div>
          <div className="friend-overall-stats">
            <div className="stat-item">
              <span className="stat-label">Total Points</span>
              <span className="stat-value">{summary.totalPoints}</span>
            </div>
            {summary.bestRank !== Infinity && (
              <div className="stat-item">
                <span className="stat-label">Best Rank</span>
                <span className="stat-value">#{summary.bestRank}</span>
              </div>
            )}
          </div>
        </div>
        <div className="friend-overall-rank">
          {summary.overallRank ? `#${summary.overallRank}` : '-'}
        </div>
      </div>

      <div className="friend-games-grid">
        {gamePerformance.map(perf => (
          <div key={perf.id} className="friend-game-item">
            <div className="game-title-mini" title={perf.title}>{perf.title}</div>
            <div className="game-perf-mini">
              {perf.rank ? (
                <>
                  <span className="game-rank-mini">#{perf.rank}</span>
                  <span className="game-score-mini">{perf.score?.toLocaleString()}</span>
                </>
              ) : (
                <span className="unranked-badge">Unranked</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FriendCard;
